// Phase 13 — AI-augmented Today hero card.
// Fetches the AI daily brief from /api/intelligence/brief and displays
// it as a warm personalized summary above the pinned-today items.

import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDailyDeck } from '../../hooks/useDailyDeck.js';
import { copy } from '@dailydeck/shared/copy';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Skeleton } from '../../components/ui/Skeleton.js';

function formatToday(): string {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export function TodayHero(): JSX.Element {
  const deckQuery = useDailyDeck();
  const briefQuery = useQuery({
    queryKey: ['intelligence', 'brief'],
    queryFn: async () => {
      const res = await fetch('/api/intelligence/brief', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('brief unavailable');
      const data = await res.json();
      return data.text as string;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const pinnedItems = React.useMemo(() => {
    if (!deckQuery.data) return [];
    const section = deckQuery.data.sections.find((s) => s.kind === 'pinned-today');
    if (!section) return [];
    return section.items.filter(
      (item): item is Extract<typeof item, { kind: 'pinned-today' }> =>
        item.kind === 'pinned-today',
    );
  }, [deckQuery.data]);

  const isLoading = deckQuery.isPending;

  if (isLoading) {
    return (
      <Card data-testid="today-hero-loading">
        <CardContent className="space-y-3 py-5">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (deckQuery.isError) {
    return (
      <Card data-testid="today-hero-error">
        <CardContent className="py-5">
          <p className="text-title-3 font-semibold text-text-primary mb-2">
            TODAY — {formatToday()}
          </p>
          <p className="text-body text-text-secondary">
            {copy.errors.network_unavailable}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="today-hero">
      <CardContent className="py-5 space-y-4">
        <p className="text-title-3 font-semibold text-text-primary">
          TODAY — {formatToday()}
        </p>

        {/* AI Daily Brief */}
        {briefQuery.isSuccess && briefQuery.data && (
          <p className="text-body text-text-secondary leading-relaxed">
            {briefQuery.data}
          </p>
        )}
        {briefQuery.isPending && (
          <Skeleton className="h-10 w-full" />
        )}

        {/* Pinned items */}
        {pinnedItems.length === 0 ? (
          <p className="text-body text-text-secondary">
            Nothing urgent today.
          </p>
        ) : (
          <ul className="space-y-2" role="list">
            {pinnedItems.map((item, i) => (
              <li
                key={`pinned-${i}`}
                className="flex items-center justify-between gap-4 rounded-md bg-surface-base px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-body-strong text-text-primary truncate">
                    {item.title}
                  </p>
                  {item.subtitle && (
                    <p className="text-caption text-text-secondary truncate">
                      {item.subtitle}
                    </p>
                  )}
                </div>
                {item.occursOn && (
                  <span className="shrink-0 text-caption text-text-muted tabular-nums">
                    {item.occursOn}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
